import { useEffect, useState, useMemo } from "react";
import { crmApi } from "../../lib/crm";
import { Activity, MousePointerClick, Clock4, Zap, Users, RefreshCw, Award, Wifi, WifiOff, AlertTriangle, Bell } from "lucide-react";

function fmtSeconds(s) {
  if (!s || s <= 0) return "0m";
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  if (h > 0) return `${h}h ${m}m`;
  return `${m}m`;
}

function fmtRelative(seconds) {
  if (seconds == null) return "—";
  if (seconds < 60) return `${seconds}s ago`;
  const m = Math.floor(seconds / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
}

export default function CRMAgentActivity() {
  const [days, setDays] = useState(30);
  const [data, setData] = useState(null);
  const [alerts, setAlerts] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const [r1, r2] = await Promise.all([
        crmApi.get(`/admin/agent-activity?days=${days}`),
        crmApi.get(`/admin/agent-activity/alerts`),
      ]);
      setData(r1.data);
      setAlerts(r2.data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    const id = setInterval(load, 60_000); // auto-refresh every 60s
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [days]);

  const totals = data?.totals || { agents: 0, online: 0, clicks: 0, session_seconds: 0, actions: 0 };
  const rows = data?.rows || [];

  const topAgent = useMemo(() => {
    if (!rows.length) return null;
    // "most active" = highest composite score (clicks + actions*5 + minutes)
    let best = null;
    let bestScore = -1;
    rows.forEach((r) => {
      const s = r.clicks + r.actions * 5 + r.session_seconds / 60;
      if (s > bestScore) { bestScore = s; best = r; }
    });
    return best;
  }, [rows]);

  // Max values for the bar charts
  const maxClicks = Math.max(1, ...rows.map((r) => r.clicks));
  const maxActions = Math.max(1, ...rows.map((r) => r.actions));
  const maxSession = Math.max(1, ...rows.map((r) => r.session_seconds));

  return (
    <div className="space-y-6 p-6" data-testid="agent-activity-page">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-3xl font-bold tracking-tight flex items-center gap-3">
            <Activity className="w-7 h-7 text-cyan-400" />
            Agent activity
          </h1>
          <p className="text-sm text-white/50 mt-1">
            Who's actually grinding — live time-in-app, UI clicks and logged actions per agent.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <select
            value={days}
            onChange={(e) => setDays(Number(e.target.value))}
            data-testid="activity-days-select"
            className="bg-white/5 border border-white/10 rounded-md px-3 py-1.5 text-sm text-white"
          >
            <option value={1}>Today</option>
            <option value={7}>Last 7 days</option>
            <option value={30}>Last 30 days</option>
            <option value={90}>Last 90 days</option>
          </select>
          <button
            type="button"
            onClick={load}
            data-testid="activity-refresh"
            className="px-3 py-1.5 rounded-md bg-cyan-400/15 text-cyan-300 border border-cyan-400/30 hover:bg-cyan-400/25 text-sm font-semibold flex items-center gap-1.5"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} /> Refresh
          </button>
        </div>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <Kpi label="Agents" value={totals.agents} sub={`${totals.online} online now`} icon={<Users className="w-4 h-4" />} tone="indigo" testid="kpi-agents" />
        <Kpi label="Total clicks" value={totals.clicks.toLocaleString()} icon={<MousePointerClick className="w-4 h-4" />} tone="cyan" testid="kpi-clicks" />
        <Kpi label="Time in app" value={fmtSeconds(totals.session_seconds)} icon={<Clock4 className="w-4 h-4" />} tone="amber" testid="kpi-time" />
        <Kpi label="Actions" value={totals.actions.toLocaleString()} icon={<Zap className="w-4 h-4" />} tone="emerald" testid="kpi-actions" />
        <Kpi label="Online now" value={totals.online} sub={`of ${totals.agents}`} icon={<Wifi className="w-4 h-4" />} tone="emerald" testid="kpi-online" />
      </div>

      {/* Top performer highlight */}
      {topAgent && (
        <div
          className="rounded-xl border border-cyan-500/30 bg-gradient-to-r from-cyan-500/10 via-blue-500/8 to-cyan-500/10 p-5 flex items-center justify-between flex-wrap gap-4"
          data-testid="top-agent-card"
        >
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-xl bg-cyan-400/20 border border-cyan-400/40 flex items-center justify-center">
              <Award className="w-6 h-6 text-cyan-300" />
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-[0.18em] text-cyan-300 font-semibold">
                Most active agent · last {days} day{days > 1 ? "s" : ""}
              </div>
              <div className="text-xl font-bold mt-0.5">{topAgent.full_name}</div>
              <div className="text-xs text-white/50">{topAgent.email}</div>
            </div>
          </div>
          <div className="flex items-center gap-6 text-sm">
            <Stat label="Clicks" value={topAgent.clicks.toLocaleString()} highlight="cyan" />
            <Stat label="Time" value={fmtSeconds(topAgent.session_seconds)} highlight="amber" />
            <Stat label="Actions" value={topAgent.actions} highlight="emerald" />
            <Stat label="Active days" value={`${topAgent.days_active}/${days}`} />
          </div>
        </div>
      )}

      {/* Automated alerts */}
      {alerts && alerts.alerts && alerts.alerts.length > 0 && (
        <section
          className="rounded-xl border border-rose-500/25 bg-gradient-to-br from-rose-500/5 to-amber-500/5 overflow-hidden"
          data-testid="alerts-section"
        >
          <div className="px-5 py-3 border-b border-white/10 flex items-center justify-between flex-wrap gap-2">
            <div className="flex items-center gap-2">
              <Bell className="w-4 h-4 text-rose-300" />
              <h2 className="text-sm font-bold uppercase tracking-[0.15em] text-white/80">
                Auto-alerts · take action
              </h2>
            </div>
            <div className="flex items-center gap-3 text-xs">
              {alerts.counts.red > 0 && (
                <span className="text-rose-300 font-semibold">{alerts.counts.red} red</span>
              )}
              {alerts.counts.amber > 0 && (
                <span className="text-amber-300 font-semibold">{alerts.counts.amber} amber</span>
              )}
              <span className="text-white/40">{alerts.counts.total} total</span>
            </div>
          </div>
          <ul className="divide-y divide-white/5" data-testid="alerts-list">
            {alerts.alerts.map((a, i) => (
              <li
                key={`${a.agent_id}-${a.code}-${i}`}
                className="px-5 py-3 flex items-start gap-3 hover:bg-white/[0.02]"
                data-testid={`alert-${a.code}`}
              >
                <span
                  className={`shrink-0 mt-0.5 inline-block w-1.5 h-1.5 rounded-full ${
                    a.severity === "red" ? "bg-rose-400 animate-pulse" :
                    a.severity === "amber" ? "bg-amber-400" : "bg-sky-400"
                  }`}
                />
                <AlertTriangle
                  className={`w-4 h-4 mt-0.5 shrink-0 ${
                    a.severity === "red" ? "text-rose-300" :
                    a.severity === "amber" ? "text-amber-300" : "text-sky-300"
                  }`}
                />
                <div className="flex-1 min-w-0">
                  <div className="font-semibold text-sm text-white">{a.title}</div>
                  <div className="text-xs text-white/55 mt-0.5">{a.detail}</div>
                </div>
                <span
                  className={`shrink-0 text-[9px] uppercase tracking-wider font-bold px-2 py-0.5 rounded border ${
                    a.severity === "red" ? "bg-rose-500/15 text-rose-300 border-rose-500/30" :
                    a.severity === "amber" ? "bg-amber-500/15 text-amber-300 border-amber-500/30" :
                    "bg-sky-500/15 text-sky-300 border-sky-500/30"
                  }`}
                >
                  {a.severity}
                </span>
              </li>
            ))}
          </ul>
        </section>
      )}

      {/* Ranking table */}
      <section className="rounded-xl border border-white/10 bg-white/[0.02] overflow-hidden">
        <div className="px-5 py-3 border-b border-white/10 flex items-center justify-between">
          <h2 className="text-sm font-bold uppercase tracking-[0.15em] text-white/70">
            Ranking
          </h2>
          <span className="text-xs text-white/40">Online first, then by UI clicks</span>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-[10px] uppercase tracking-[0.15em] text-white/40 border-b border-white/10">
              <tr>
                <th className="text-left px-4 py-3">#</th>
                <th className="text-left px-3 py-3">Agent</th>
                <th className="text-left px-3 py-3">Status</th>
                <th className="text-left px-3 py-3">Last seen</th>
                <th className="text-right px-3 py-3">Time in app</th>
                <th className="text-right px-3 py-3">UI clicks</th>
                <th className="text-right px-3 py-3">Actions</th>
                <th className="text-center px-3 py-3">Trend (7d)</th>
                <th className="text-right px-3 py-3">Days active</th>
              </tr>
            </thead>
            <tbody data-testid="agent-activity-body">
              {rows.length === 0 && (
                <tr>
                  <td colSpan={9} className="text-center text-white/40 px-4 py-8">
                    No agents yet. Activity will appear here as soon as someone logs in.
                  </td>
                </tr>
              )}
              {rows.map((r, i) => (
                <tr key={r.agent_id} className="border-b border-white/5 hover:bg-white/[0.02]" data-testid={`activity-row-${i + 1}`}>
                  <td className="px-4 py-3 text-white/40 mono">{i + 1}</td>
                  <td className="px-3 py-3">
                    <div className="font-semibold">{r.full_name}</div>
                    <div className="text-xs text-white/40">{r.email}</div>
                  </td>
                  <td className="px-3 py-3">
                    {r.is_online ? (
                      <span className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 rounded border bg-emerald-500/15 text-emerald-300 border-emerald-500/30">
                        <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" /> Online
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-wider px-2 py-0.5 rounded border bg-white/5 text-white/40 border-white/15">
                        <WifiOff className="w-2.5 h-2.5" /> Offline
                      </span>
                    )}
                  </td>
                  <td className="px-3 py-3 text-white/50 whitespace-nowrap">
                    {fmtRelative(r.seconds_since)}
                  </td>
                  <td className="text-right px-3 py-3">
                    <BarCell value={r.session_seconds} max={maxSession} display={fmtSeconds(r.session_seconds)} tone="amber" />
                  </td>
                  <td className="text-right px-3 py-3">
                    <BarCell value={r.clicks} max={maxClicks} display={r.clicks.toLocaleString()} tone="cyan" />
                  </td>
                  <td className="text-right px-3 py-3">
                    <BarCell value={r.actions} max={maxActions} display={r.actions.toLocaleString()} tone="emerald" />
                  </td>
                  <td className="px-3 py-3">
                    <Sparkline points={r.spark_clicks || []} tone="cyan" />
                  </td>
                  <td className="text-right px-3 py-3 mono">
                    <span className="text-white/70">{r.days_active}</span>
                    <span className="text-white/30 text-xs">/{days}</span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      <p className="text-xs text-white/40 text-center pt-2">
        Auto-refreshes every 60 seconds. Click tracking is batched every 30s, so very recent clicks may take a moment to appear.
      </p>
    </div>
  );
}

function Kpi({ label, value, sub, icon, tone = "indigo", testid }) {
  const tones = {
    indigo: "from-indigo-500/10 to-blue-500/10 border-indigo-500/20 text-indigo-300",
    cyan: "from-cyan-500/10 to-sky-500/10 border-cyan-500/20 text-cyan-300",
    amber: "from-amber-500/10 to-yellow-500/10 border-amber-500/20 text-amber-300",
    emerald: "from-emerald-500/10 to-green-500/10 border-emerald-500/20 text-emerald-300",
  };
  return (
    <div className={`rounded-xl border bg-gradient-to-br p-4 ${tones[tone]}`} data-testid={testid}>
      <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.15em] font-semibold opacity-80">
        {icon} {label}
      </div>
      <div className="text-2xl font-bold mt-2 text-white">{value}</div>
      {sub && <div className="text-xs opacity-60 mt-1">{sub}</div>}
    </div>
  );
}

function Stat({ label, value, highlight }) {
  const colors = {
    cyan: "text-cyan-300",
    amber: "text-amber-300",
    emerald: "text-emerald-300",
  };
  return (
    <div className="text-center">
      <div className="text-[10px] uppercase tracking-wider text-white/40">{label}</div>
      <div className={`text-lg font-bold mt-0.5 ${highlight ? colors[highlight] : "text-white"}`}>
        {value}
      </div>
    </div>
  );
}

function BarCell({ value, max, display, tone = "cyan" }) {
  const pct = Math.max(0, Math.min(100, (value / max) * 100));
  const tones = {
    cyan: "from-cyan-400 to-cyan-500",
    amber: "from-amber-400 to-amber-500",
    emerald: "from-emerald-400 to-emerald-500",
  };
  return (
    <div className="flex items-center justify-end gap-2">
      <div className="w-20 h-1.5 rounded-full bg-white/10 overflow-hidden">
        <div className={`h-full bg-gradient-to-r ${tones[tone]}`} style={{ width: `${pct}%` }} />
      </div>
      <span className="mono text-xs w-16 text-right">{display}</span>
    </div>
  );
}

function Sparkline({ points, tone = "cyan" }) {
  // Render an SVG mini line chart of the last 7 daily values.
  // The bar at the right edge is "today" — a brighter dot highlights it.
  const arr = (points && points.length ? points : new Array(7).fill(0));
  const W = 90;
  const H = 28;
  const max = Math.max(1, ...arr);
  const stepX = W / Math.max(1, arr.length - 1);
  const toY = (v) => H - 2 - ((v / max) * (H - 6));
  const path = arr.map((v, i) => `${i === 0 ? "M" : "L"}${(i * stepX).toFixed(1)},${toY(v).toFixed(1)}`).join(" ");
  const areaPath = `${path} L${((arr.length - 1) * stepX).toFixed(1)},${H} L0,${H} Z`;
  const strokes = { cyan: "#67e8f9", amber: "#fbbf24", emerald: "#6ee7b7" };
  const fills = { cyan: "rgba(103,232,249,0.18)", amber: "rgba(251,191,36,0.18)", emerald: "rgba(110,231,183,0.18)" };
  const todayY = toY(arr[arr.length - 1]);
  const todayX = (arr.length - 1) * stepX;
  const trend = arr[arr.length - 1] - (arr[0] || 0);
  return (
    <div className="flex items-center justify-center gap-2" title={`Last 7 days: ${arr.join(", ")}`}>
      <svg width={W} height={H} viewBox={`0 0 ${W} ${H}`}>
        <path d={areaPath} fill={fills[tone]} />
        <path d={path} fill="none" stroke={strokes[tone]} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
        <circle cx={todayX} cy={todayY} r="2.5" fill={strokes[tone]} />
      </svg>
      <span className={`text-[10px] mono w-6 text-right ${trend > 0 ? "text-emerald-300" : trend < 0 ? "text-rose-300" : "text-white/40"}`}>
        {trend > 0 ? "↑" : trend < 0 ? "↓" : "·"}
      </span>
    </div>
  );
}
